import React, { useEffect, useMemo, useState } from "react";
import SFIcon from "@/custom_components/SFIcon/SFIcon";
import { helpIconFor } from "./helpIcons";
import { fetchVimeoThumbnail, parseVideoLink, safeUrl, staticThumbnailUrl } from "./helpUtils";
import classes from "./Help.module.css";

/**
 * Resolves a poster frame for a tutorial link.
 *
 * YouTube posters are derivable from the video id, so they render on first
 * paint. Vimeo needs an oEmbed round trip, so those fill in a moment later —
 * and either can fail (private video, deleted, offline), which drops the card
 * back to its icon tile rather than leaving a broken image.
 */
const useVideoThumbnail = (link) => {
  const video = useMemo(() => parseVideoLink(link), [link]);
  const [src, setSrc] = useState(() => staticThumbnailUrl(video));
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBroken(false);

    const immediate = staticThumbnailUrl(video);
    setSrc(immediate);

    if (!immediate && video?.provider === "vimeo") {
      fetchVimeoThumbnail(video).then((url) => {
        if (!cancelled) setSrc(url);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [video]);

  return { isVideo: !!video, src: broken ? null : src, onError: () => setBroken(true) };
};

const TutorialCard = ({ tutorial }) => {
  const { isVideo, src, onError } = useVideoThumbnail(tutorial.link);

  // `link` is unvalidated server-side, so a junk value renders as a plain,
  // non-clickable row instead of a dead or unsafe anchor.
  const url = safeUrl(tutorial.link);

  const thumb = (
    <span className={classes.thumb}>
      {src ? (
        <img src={src} alt="" loading="lazy" className={classes.thumbImg} onError={onError} />
      ) : (
        <span className={classes.thumbFallback}>
          <SFIcon name={helpIconFor(tutorial.iconId)} size={20} />
        </span>
      )}
      {isVideo && (
        <span className={classes.playBadge} aria-hidden="true">
          <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
            <path d="M3 1.6 10 6l-7 4.4z" fill="currentColor" />
          </svg>
        </span>
      )}
    </span>
  );

  const body = (
    <>
      {thumb}
      <span className={classes.tutorialText}>
        <span className={classes.tutorialTitle}>{tutorial.title}</span>
        {/* subText defaults to "" server-side, so it's always a string but
            often empty — skip the element entirely rather than render a
            zero-height node that still eats the gap above it. */}
        {tutorial.subText ? (
          <span className={classes.tutorialSub}>{tutorial.subText}</span>
        ) : null}
        {!url && <span className={classes.tutorialBroken}>Link unavailable</span>}
      </span>
      {url && <SFIcon name="share" size={14} className={classes.tutorialGo} />}
    </>
  );

  if (!url) {
    return <div className={`${classes.tutorialCard} ${classes.tutorialCardDead}`}>{body}</div>;
  }

  return (
    <a
      className={classes.tutorialCard}
      href={url.href}
      target="_blank"
      rel="noopener noreferrer"
      title={tutorial.title}
    >
      {body}
    </a>
  );
};

export default TutorialCard;
